import { Button } from "@tento-ui/components/button/button";
import { Toast } from "@tento-ui/components/toast/toast";
import { Storybook } from "@tento-ui/ui/storybook";
import { TimezoneConfirmationModal } from "@tento-ui/components/calendar-month/timezone-confirmation-modal";
import { DateTime, DateTimeRegion, TimezoneRegion, Tzname } from "@tento-chrono";

// Helper to build timezone options programmatically with start/end times
async function buildTimezoneOptions(
  eventStartUtc: DateTime<"utc">,
  eventEndUtc: DateTime<"utc">,
  dayOffset: number,
  eventTz: Tzname,
  calendarTz: Tzname,
  browserTz: Tzname,
): Promise<TimezoneConfirmationModal.TimezoneOption[]> {
  const calendarTzRegion = await TimezoneRegion.get(calendarTz);

  const buildOption = async (
    label: string,
    tzName: Tzname,
    isDefault?: boolean,
  ): Promise<TimezoneConfirmationModal.TimezoneOption> => {
    const givenTzRegion = await TimezoneRegion.get(tzName);

    // Calculate new start time
    const startWall = givenTzRegion.toTz(eventStartUtc);
    const newStartWall = startWall.add({ days: dayOffset });
    const newStartUtc = givenTzRegion.toWallClock(newStartWall.ndt).asUtc();
    const newStartLocal = calendarTzRegion.toTz(newStartUtc);

    // Calculate new end time
    const endWall = givenTzRegion.toTz(eventEndUtc);
    const newEndWall = endWall.add({ days: dayOffset });
    const newEndUtc = givenTzRegion.toWallClock(newEndWall.ndt).asUtc();
    const newEndLocal = calendarTzRegion.toTz(newEndUtc);

    const formatTime = (ndt: { time: { format: (opts: { includeSeconds: boolean; includeAmPm: boolean }) => string } }) =>
      ndt.time.format({ includeSeconds: false, includeAmPm: true });

    return {
      label,
      description: TimezoneConfirmationModal.createTimeRangeDescription(
        {
          startTime: formatTime(newStartWall.ndt),
          endTime: formatTime(newEndWall.ndt),
          tzName: tzName,
        },
        {
          startTime: formatTime(newStartUtc),
          endTime: formatTime(newEndUtc),
          tzName: "UTC",
        },
        {
          startTime: formatTime(newStartLocal.ndt),
          endTime: formatTime(newEndLocal.ndt),
          tzName: calendarTz,
        },
      ),
      value: tzName,
      isDefault,
      resolvedTime: DateTimeRegion.Range.fromSingleTz(
        new DateTime.Range(newStartUtc, newEndUtc),
        calendarTzRegion,
      ),
    };
  };

  return Promise.all([
    buildOption("Event's original timezone", eventTz),
    buildOption("Your calendar timezone", calendarTz, true),
    buildOption("Your current location", browserTz),
  ]);
}

export class TimezoneConfirmationModalStoryBook extends Storybook.Default {
  title(): string {
    return "Timezone Confirmation Modal";
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "Show Modal (Interactive)",
        render: () => {
          return Button.makeWithLight("Move Event", {
            type: "primary",
            onClick: async () => {
              const eventTz = "America/New_York" as Tzname;
              const calendarTz = "America/Los_Angeles" as Tzname;
              const browserTz = Intl.DateTimeFormat().resolvedOptions()
                .timeZone as Tzname;

              // Simulate event: 9:00 AM - 10:00 AM EST (2:00 PM - 3:00 PM UTC)
              const eventStartUtc = DateTime.utc
                .now()
                .ndt.date.withTime()
                .asUtc()
                .add({ hrs: 14 });
              const eventEndUtc = eventStartUtc.add({ hrs: 1 });

              const options = await buildTimezoneOptions(
                eventStartUtc,
                eventEndUtc,
                3, // Moving 3 days forward
                eventTz,
                calendarTz,
                browserTz,
              );

              const result = await TimezoneConfirmationModal.show({
                title: "Select Timezone",
                description: "Please specify a timezone for the new event time.",
                eventTitle: "Team Standup",
                eventIcon: "📅",
                eventIconBg: "var(--gray-dimmed)",
                options,
              });

              if (result) {
                Toast.info(`Selected timezone: ${result.selectedTimezone}`);
              } else {
                Toast.info("Cancelled");
              }
            },
          });
        },
      },
      {
        title: "Direct Render (Two Options)",
        render: () => {
          const $modal = TimezoneConfirmationModal.make();

          // Build options asynchronously and bind
          const calendarTz = "America/Los_Angeles" as Tzname;
          const browserTz = "Europe/Berlin" as Tzname;

          // Simulate event: 10:00 AM - 11:30 AM PST
          const eventStartUtc = DateTime.utc.now().ndt.date.withTime().asUtc().add({ hrs: 18 });
          const eventEndUtc = eventStartUtc.add({ hrs: 1, mins: 30 });

          buildTimezoneOptions(
            eventStartUtc,
            eventEndUtc,
            5, // Moving 5 days forward
            calendarTz, // No separate event timezone
            calendarTz,
            browserTz,
          ).then((allOptions) => {
            // Filter to just calendar and browser timezones
            const options = allOptions.filter(
              (o) => o.value === calendarTz || o.value === browserTz,
            );

            $modal.bind(
              {
                title: "Select Timezone",
                description: "Please specify a timezone for the new event time.",
                eventTitle: "Brunch with Sally",
                eventIcon: "🍳",
                eventIconBg: "var(--yellow-dimmed)",
                options,
              },
              (result) => {
                if (result) {
                  Toast.info(`Selected: ${result.selectedTimezone}`);
                } else {
                  Toast.info("Cancelled");
                }
              },
            );
          });

          return $modal;
        },
      },
    ];
  }
}
